import dayjs, { Dayjs } from 'dayjs'
import { TimeRange } from 'features/systemLens'
import { TimeSeriesService } from 'features/timeSeries/timeSeriesService'
import {
  ABSOLUTE_MIN_TIME_RANGE_SPAN_MS,
  clampTimeRange,
} from 'features/timeSeries/utils/timeRangeClamp'
import {
  panTimeRange,
  zoomTimeRange,
} from 'features/timeSeries/utils/timeRangeNavigation'

export type TimeRangeNavigationDirection = 'forward' | 'backward'

export interface NavigateTimeRangeOptions {
  /**
   * Narrowest span the zoom-in control may produce. Defaults to the floor for a
   * 60s PI tag; pass one derived from the loaded data where the cadence is
   * known (see `sampleCadence.estimateSampleIntervalMs`).
   */
  minSpanMs?: number
  /**
   * Pens on the chart the navigated range is for. The interval baked into the
   * returned range shares the bucket budget across pens
   * (`TimeSeriesService.getIntervalForPenCount`) and must match what
   * `buildTimeSeriesRequest` computes for the same range, or the fetch layer
   * sees two different request signatures for one navigation. Defaults to 1
   * (the full 10,000-bucket budget) for callers without a pen concept.
   */
  penCount?: number
}

export function formatDuration(from: Dayjs, to: Dayjs) {
  const hours = to.diff(from, 'hours')
  const days = to.diff(from, 'days')
  const months = to.diff(from, 'months')
  const years = to.diff(from, 'years')

  if (hours < 24) {
    return `${hours}h`
  } else if (days < 31) {
    return `${days}d`
  } else if (months < 12) {
    return `${months}mo`
  } else {
    return `${years}y`
  }
}

/**
 * The range the arrow / zoom controls navigate to.
 *
 * `zoom` selects the gesture; `direction` selects its sense (with `zoom`,
 * 'forward' is zoom in and 'backward' is zoom out).
 *
 * The math lives in `timeRangeNavigation` -- pan translates by
 * `PAN_STEP_FRACTION` without changing the span, zoom scales by
 * `ZOOM_STEP_FACTOR` about the midpoint, and each handles the `now` edge in the
 * way that suits it. This function owns only the dayjs/`TimeRange` plumbing and
 * the final `clampTimeRange`, which is what bounds the span: zoom in previously
 * halved the window on every click with no floor at all, so twelve clicks from
 * 2h reached ~2 seconds of a tag that samples once a minute.
 */
export function getNavigatedTimeRange(
  direction: TimeRangeNavigationDirection,
  zoom: boolean = false,
  fromTime: Dayjs,
  toTime: Dayjs,
  now: Dayjs = dayjs(),
  options: NavigateTimeRangeOptions = {}
): TimeRange {
  const range = { from: fromTime.valueOf(), to: toTime.valueOf() }
  const nowMs = now.valueOf()

  const navigated = zoom
    ? zoomTimeRange(range, direction === 'forward' ? 'in' : 'out', nowMs)
    : panTimeRange(range, direction, nowMs)

  // A degenerate current range (from === to, or a `to` in the future) can reach
  // us from a saved chart or a URL param, so the same sanitation the
  // pixel-derived zoom paths use applies here too.
  //
  // The span floor applies to zoom only. Zoom is the gesture that *chooses* a
  // span, so it is the one that can choose an unusable one. Pan inherits
  // whatever span the user already has; flooring it there would mean pressing an
  // arrow silently widened the window, which is both surprising and a change pan
  // has no business making. Pan still gets the absolute bound, because a
  // sub-minute request is degenerate no matter which gesture produced it.
  const clamped =
    clampTimeRange(navigated.from, navigated.to, {
      now: nowMs,
      minSpanMs: zoom ? options.minSpanMs : ABSOLUTE_MIN_TIME_RANGE_SPAN_MS,
    }) ?? navigated

  const newRangeSeconds = (clamped.to - clamped.from) / 1000
  const { interval, interval_unit } = TimeSeriesService.getIntervalForPenCount(
    newRangeSeconds,
    options.penCount ?? 1
  )

  return {
    from: dayjs(clamped.from).toISOString(),
    to: dayjs(clamped.to).toISOString(),
    preset: 'custom',
    interval,
    intervalUnit: interval_unit,
  }
}

export function navigateTimeRange(
  direction: TimeRangeNavigationDirection,
  zoom: boolean = false,
  fromTime: Dayjs,
  toTime: Dayjs,
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>,
  options: NavigateTimeRangeOptions = {}
) {
  setTimeRange(
    getNavigatedTimeRange(direction, zoom, fromTime, toTime, dayjs(), options)
  )
}

// Check if we can navigate forward (not at current time)
export const canNavigateForward = (toTime: Dayjs) => {
  const now = dayjs()
  // Allow forward navigation if end time is more than 5 minutes before now
  return toTime.isBefore(now.subtract(5, 'minutes'))
}
